import IndividualFeeTable, {
  GroupedFees,
  GroupedFeesWithDefaultTransactions,
} from './IndividualFeeTable';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { ApprovalSummaryData } from '..';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { FundingSource } from 'further-types/lp/investment';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import Typography from '@mui/material/Typography';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { History } from '@mui/icons-material';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure } from 'further-ui/hooks';
import { useState } from 'react';
import { sumBy } from 'lodash';
import { SortOrder } from 'further-types/utils';
import { styled, Box } from '@mui/material';

const IndividualFeeTableWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

type FeeOverride = {
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  fundingSource: FundingSource;
  feeIncTaxLcy: number;
};

interface Props {
  currencyLcy?: CurrencyCode;
  fees: GroupedFeesWithDefaultTransactions;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  feeType: ListLpFeesCategory;
  selectedFeeGroupId?: string;
  setApprovalSummaryData?: (data: ApprovalSummaryData) => void;
}

const notFoundMessages = {
  [ListLpFeesCategory.FutureUnapprovedFees]: 'No future unapproved fees found.',
  [ListLpFeesCategory.DueUnapprovedFees]:
    'No unapproved fees found that are due for processing.',
  [ListLpFeesCategory.ApprovedFees]: 'No approved fees found.',
};

const FeeList: React.FC<Props> = ({
  fees,
  currentPage,
  currencyLcy,
  onPageChange,
  feeType,
  selectedFeeGroupId,
  setApprovalSummaryData,
}) => {
  const canApproveFees = feeType !== ListLpFeesCategory.ApprovedFees;
  const [canEditFees] = useHasPermissions([RolePermission.LpEditFee]);
  const unapproveDialog = useDisclosure();
  const { selectedVehicleId } = useLayoutContext();
  const { groupedFees, defaultTransactions, totalCount } = fees;
  const notFoundMessage = notFoundMessages[feeType];
  const { expandedRows, toggleRowExpansion } = useExpandedRows(
    canApproveFees ? [groupedFees[0]?.id] : undefined,
  );

  const [overrides, setOverrides] = useState<Record<string, FeeOverride>>({});

  const enrichedFeeGroups = groupedFees.map((feeGroup) => {
    const isFromEqualisation = feeGroup.lpFees.some(
      (fee) => fee.isFromEqualisation,
    );
    const disableUnapprove = feeGroup.lpFees.some(
      (fee) => fee.disableUnapprove,
    );
    return {
      ...feeGroup,
      isFromEqualisation,
      disableUnapprove,
    };
  });

  const applyOverride = (
    fees: GroupedFees[number]['lpFees'],
    override: Record<string, FeeOverride>,
  ) =>
    fees.map((fee) => {
      const o = override[fee._id];
      return o ? { ...fee, ...o } : fee;
    });

  const rows = enrichedFeeGroups.map((feeGroup) => {
    const lpFees = applyOverride(feeGroup.lpFees, overrides);
    const initialTotal = sumBy(lpFees, (fee) => fee.feeIncTaxLcy);
    return { ...feeGroup, lpFees, initialTotal };
  });

  const mutationOpts = useHandleTrpcMutation();
  const unapproveFeesMutation = api.lpFee.unapprove.useMutation(
    mutationOpts({
      successMessage: 'Fees unapproved successfully.',
    }),
  );

  const handleUnapproveFees = (feeActionIds: Array<string>) => {
    unapproveFeesMutation.mutate({
      vehicleId: selectedVehicleId ?? '',
      feeActionIds,
    });
  };

  const handleSelectFee = ({
    id,
    date,
    fees,
  }: {
    date: CalendarDay;
    id: string;
    fees: (typeof rows)[number]['lpFees'];
  }) => {
    if (selectedFeeGroupId === id) {
      setApprovalSummaryData?.({
        id: '',
        date: new CalendarDay(new Date()),
        fullFeeData: [],
        defaultTransactions: {
          insideCommitment: [],
          outsideCommitment: [],
        },
        feeType,
      });
      return;
    }
    setApprovalSummaryData?.({
      id,
      date,
      fullFeeData: fees,
      defaultTransactions,
      feeType,
    });
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'feeName',
      label: 'Fee',
      weight: 0.8,
      render: (_, rowIndex) =>
        canApproveFees ? (
          <Typography variant="inherit">Management fees</Typography>
        ) : (
          <ColorIndicatorWithText listIndex={rowIndex} text="Management fees" />
        ),
    },
    {
      key: 'accrualDate',
      weight: 0.5,
      label: 'Fee date',
      render: ({ accrualDate }) => accrualDate.toReadable(),
    },
    {
      key: 'amount',
      weight: 0.5,
      label: 'Amount',
      render: ({ initialTotal }) =>
        numberToCurrencyString(initialTotal, {
          currency: currencyLcy,
        }),
    },
  ];

  return (
    <>
      <Table
        pagination={{
          orderBy: 'accrualDate',
          order: SortOrder.Desc,
          page: currentPage ?? 1,
          handleChangePage: (_, page) => {
            setOverrides({});
            onPageChange?.(page + 1);
          },
          rowsPerPage: 10,
          handleChangeRowsPerPage: () => {},
          handleRequestSort: () => {},
        }}
        checkboxes={
          canApproveFees
            ? {
                isCheckedRow: ({ id }) => selectedFeeGroupId === id,
                onRowCheckedToggle: ({ id, lpFees, accrualDate }) => {
                  handleSelectFee({ fees: lpFees, date: accrualDate, id });
                },
                variant: 'radio',
              }
            : undefined
        }
        rowsPerPageOptions={[]}
        hidePaginationForSinglePage
        rowsTotalCount={totalCount}
        columns={columns}
        rows={rows}
        emptyMessage={notFoundMessage}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        isExpandableRow={() => true}
        actions={[
          {
            icon: History,
            label: 'Unapprove',
            disabled: (row) => {
              return row.isFromEqualisation || row.disableUnapprove;
            },
            hidden: canApproveFees || !canEditFees,
            tooltip: (row) => {
              if (!row.disableUnapprove) return undefined;
              return row.isFromEqualisation
                ? 'It is not possible to unapprove fees approved via equalisation.'
                : 'It is not possible to unapprove fees once capital has been called.';
            },
            onClick: (row) => {
              unapproveDialog.stageAction({
                feeActionIds: Array.from(
                  new Set(
                    row.lpFees
                      .map((fee) => fee.feeActionId ?? '')
                      .filter((id) => id !== ''),
                  ),
                ),
              });
              unapproveDialog.onOpen();
            },
          },
        ]}
        getExpandedContent={(feeGroup) => (
          <IndividualFeeTableWrapper>
            <IndividualFeeTable
              onFeeChange={({ id, ...override }) => {
                setOverrides((prev) => {
                  const next = { ...prev, [id]: override };
                  if (selectedFeeGroupId === feeGroup.id) {
                    setApprovalSummaryData?.({
                      id: feeGroup.id,
                      date: feeGroup.accrualDate,
                      fullFeeData: applyOverride(feeGroup.lpFees, next),
                      defaultTransactions,
                      feeType,
                    });
                  }
                  return next;
                });
              }}
              currencyLcy={currencyLcy}
              feeList={feeGroup.lpFees}
              feeType={feeType}
            />
          </IndividualFeeTableWrapper>
        )}
      />
      {!canApproveFees && (
        <ConfirmDialog
          maxWidth="md"
          open={unapproveDialog.isOpen}
          onClose={unapproveDialog.onClose}
          title="Confirm Unapproval"
          content="Are you sure you want to unapprove these fees? This will remove the transactions from the General Ledger and move the fees back to an unapproved state."
          actions={{
            confirm: {
              onClick: () =>
                handleUnapproveFees(unapproveDialog.actionProps.feeActionIds),
            },
          }}
        />
      )}
    </>
  );
};

export default FeeList;
